import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import AdminLayout from '@/components/admin/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Search, Filter, RefreshCw, Eye, Mail, MailCheck, Ban, CheckCircle,
  Users, UserPlus, AlertTriangle, X
} from 'lucide-react';

const STATUS_OPTIONS = [
  { value: '', label: 'Todos os status' },
  { value: 'ATIVO', label: 'Ativo' },
  { value: 'INATIVO', label: 'Inativo' },
  { value: 'SUSPENSO', label: 'Suspenso' },
  { value: 'BLOQUEADO', label: 'Bloqueado' },
  { value: 'ENCERRADO', label: 'Encerrado' }
];

const STATUS_COLORS = {
  ATIVO: 'bg-green-100 text-green-700',
  INATIVO: 'bg-gray-100 text-gray-700',
  SUSPENSO: 'bg-yellow-100 text-yellow-700',
  BLOQUEADO: 'bg-red-100 text-red-700',
  ENCERRADO: 'bg-slate-100 text-slate-700'
};

export default function AdminClients() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useContext(AuthContext);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    status: searchParams.get('status') || '',
    emailVerified: searchParams.get('filter') === 'unverified' ? 'false' : ''
  });
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    unverified: 0,
    blocked: 0
  });

  useEffect(() => {
    if (!user || user.user_type !== 'admin') {
      navigate('/control-portal-clube/login');
      return;
    }
    fetchClients();
  }, [user, navigate]);

  const fetchClients = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const allClients = res.data.clients || [];
      setClients(allClients);
      
      // Calcular estatísticas
      setStats({
        total: allClients.length,
        active: allClients.filter(c => (c.status || 'ATIVO') === 'ATIVO').length,
        unverified: allClients.filter(c => !c.email_verified).length,
        blocked: allClients.filter(c => c.status === 'BLOQUEADO' || c.blocked).length
      });
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar clientes');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleResendVerification = async (clientId, e) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/resend-verification/${clientId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('E-mail de verificação reenviado!');
    } catch (error) {
      toast.error('Erro ao reenviar e-mail');
    }
  };

  const handleForceVerify = async (clientId, e) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/verify-email/${clientId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('E-mail verificado manualmente!');
      fetchClients(true);
    } catch (error) {
      toast.error('Erro ao verificar e-mail');
    }
  };

  const getInitials = (name) => {
    if (!name) return '??';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  // Filtrar clientes
  const filteredClients = clients.filter(client => {
    if (filters.search) {
      const search = filters.search.toLowerCase();
      const matches = 
        client.name?.toLowerCase().includes(search) ||
        client.email?.toLowerCase().includes(search) ||
        client.cpf_cnpj?.includes(search);
      if (!matches) return false;
    }
    if (filters.status && (client.status || 'ATIVO') !== filters.status) return false;
    if (filters.emailVerified === 'false' && client.email_verified) return false;
    if (filters.emailVerified === 'true' && !client.email_verified) return false;
    return true;
  });

  const clearFilters = () => {
    setFilters({ search: '', status: '', emailVerified: '' });
    setSearchParams({});
  };

  const hasFilters = filters.search || filters.status || filters.emailVerified;

  if (loading) {
    return (
      <AdminLayout title="Clientes" subtitle="Carregando...">
        <div className="space-y-6">
          <div className="grid grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
          </div>
          <Skeleton className="h-12" />
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-20" />)}
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Clientes</h1>
            <p className="text-slate-500">Gerencie todos os clientes cadastrados</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => fetchClients(true)} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card 
            className={`p-4 cursor-pointer transition-all ${!filters.status ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setFilters({ ...filters, status: '', emailVerified: '' })}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-xs text-slate-500">Total</p>
              </div>
            </div>
          </Card>
          
          <Card 
            className={`p-4 cursor-pointer transition-all ${filters.status === 'ATIVO' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setFilters({ ...filters, status: 'ATIVO', emailVerified: '' })}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.active}</p>
                <p className="text-xs text-slate-500">Ativos</p>
              </div>
            </div>
          </Card>

          <Card 
            className={`p-4 cursor-pointer transition-all ${filters.emailVerified === 'false' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setFilters({ ...filters, status: '', emailVerified: 'false' })}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.unverified}</p>
                <p className="text-xs text-slate-500">E-mail pendente</p>
              </div>
            </div>
          </Card>

          <Card 
            className={`p-4 cursor-pointer transition-all ${filters.status === 'BLOQUEADO' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setFilters({ ...filters, status: 'BLOQUEADO', emailVerified: '' })}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <Ban className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.blocked}</p>
                <p className="text-xs text-slate-500">Bloqueados</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card className="p-4">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por nome, e-mail ou CPF..."
                className="pl-10"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
            </div>
            <Select value={filters.status} onValueChange={(val) => setFilters({ ...filters, status: val })}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value || 'all'}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" /> Limpar
              </Button>
            )}
          </div>
        </Card>

        {/* Clients List */}
        <div className="space-y-3">
          {filteredClients.length > 0 ? (
            filteredClients.map((client) => (
              <Card 
                key={client.id}
                className="p-4 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/control-portal-clube/clients/${client.id}`)}
              >
                <div className="flex items-center gap-4">
                  <Avatar className="w-12 h-12">
                    <AvatarFallback className="bg-blue-100 text-blue-700">
                      {getInitials(client.name)}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-semibold truncate">{client.name}</p>
                      <Badge className={STATUS_COLORS[client.status] || STATUS_COLORS.ATIVO}>
                        {client.status || 'ATIVO'}
                      </Badge>
                      {!client.email_verified && (
                        <Badge className="bg-yellow-100 text-yellow-700">
                          E-mail pendente
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-500">{client.email}</p>
                    <div className="flex items-center gap-4 text-xs text-slate-400 mt-1">
                      <span>CPF: {client.cpf_cnpj || 'N/A'}</span>
                      <span>•</span>
                      <span>Cadastro: {formatDate(client.created_at)}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {!client.email_verified && (
                      <>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={(e) => handleResendVerification(client.id, e)}
                          title="Reenviar e-mail"
                        >
                          <Mail className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={(e) => handleForceVerify(client.id, e)}
                          title="Verificar manualmente"
                        >
                          <MailCheck className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                    <Button variant="ghost" size="sm">
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {client.admin_observation && (
                  <div className="mt-3 p-2 bg-amber-50 border border-amber-100 rounded text-xs text-amber-800">
                    <strong>Observação:</strong> {client.admin_observation}
                  </div>
                )}
              </Card>
            ))
          ) : (
            <Card className="p-12 text-center">
              <Users className="w-16 h-16 mx-auto mb-4 text-slate-200" />
              <h3 className="text-lg font-semibold mb-2">Nenhum cliente encontrado</h3>
              <p className="text-slate-500">
                {hasFilters 
                  ? 'Tente ajustar os filtros de busca'
                  : 'Os clientes cadastrados aparecerão aqui'}
              </p>
              {hasFilters && (
                <Button variant="outline" className="mt-4" onClick={clearFilters}>
                  Limpar filtros
                </Button>
              )}
            </Card>
          )}
        </div>

        {/* Summary */}
        {filteredClients.length > 0 && (
          <p className="text-sm text-slate-500 text-center">
            Mostrando {filteredClients.length} de {stats.total} clientes
          </p>
        )}
      </div>
    </AdminLayout>
  );
}
